import React, { useState } from 'react';
import { IPRange, IPRangeCreate, ipRangeApi } from '../services/api';
import './IPRangeForm.css';

interface IPRangeFormProps {
  officeId: number;
  ipRange?: IPRange | null;
  onClose: () => void;
  onSubmit: () => void;
}

const IPRangeForm: React.FC<IPRangeFormProps> = ({ officeId, ipRange, onClose, onSubmit }) => {
  const [formData, setFormData] = useState<IPRangeCreate>({
    office_id: officeId,
    network: ipRange?.network || '',
    subnet_mask: ipRange?.subnet_mask || 24,
    dhcp_start: ipRange?.dhcp_start || '',
    dhcp_end: ipRange?.dhcp_end || '',
    static_start: ipRange?.static_start || '',
    static_end: ipRange?.static_end || '',
    vlan_id: ipRange?.vlan_id || undefined,
    description: ipRange?.description || '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'number' ? (value ? parseInt(value) : undefined) : value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      if (ipRange) {
        await ipRangeApi.update(ipRange.id, formData);
      } else {
        await ipRangeApi.create(formData);
      }
      onSubmit();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to save IP range');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal modal-lg">
        <div className="modal-header">
          <h2>{ipRange ? 'Edit IP Range' : 'Add New IP Range'}</h2>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>
        
        <form onSubmit={handleSubmit}>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="network">Network Address*</label>
              <input
                type="text"
                id="network"
                name="network"
                value={formData.network}
                onChange={handleChange}
                placeholder="e.g., 192.168.1.0"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="subnet_mask">Subnet Mask*</label>
              <input
                type="number"
                id="subnet_mask"
                name="subnet_mask"
                value={formData.subnet_mask}
                onChange={handleChange}
                min="0"
                max="32"
                required
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="dhcp_start">DHCP Start</label>
              <input
                type="text"
                id="dhcp_start"
                name="dhcp_start"
                value={formData.dhcp_start}
                onChange={handleChange}
                placeholder="e.g., 192.168.1.100"
              />
            </div>

            <div className="form-group">
              <label htmlFor="dhcp_end">DHCP End</label>
              <input
                type="text"
                id="dhcp_end"
                name="dhcp_end"
                value={formData.dhcp_end}
                onChange={handleChange}
                placeholder="e.g., 192.168.1.200"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="static_start">Static IP Start</label>
              <input
                type="text"
                id="static_start"
                name="static_start"
                value={formData.static_start}
                onChange={handleChange}
                placeholder="e.g., 192.168.1.10"
              />
            </div>

            <div className="form-group">
              <label htmlFor="static_end">Static IP End</label>
              <input
                type="text"
                id="static_end"
                name="static_end"
                value={formData.static_end}
                onChange={handleChange}
                placeholder="e.g., 192.168.1.99"
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="vlan_id">VLAN ID</label>
            <input
              type="number"
              id="vlan_id"
              name="vlan_id"
              value={formData.vlan_id || ''}
              onChange={handleChange}
              placeholder="Optional"
            />
          </div>

          <div className="form-group">
            <label htmlFor="description">Description</label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows={3}
              placeholder="Optional description"
            />
          </div>

          {error && <div className="error">{error}</div>}

          <div className="form-actions">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Saving...' : ipRange ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default IPRangeForm;